// Tracks which changes to this device's own feedback items (status change,
// a reply from someone else, closed/reopened) the user hasn't looked at yet,
// so the sidebar can badge them and the board can mark the affected cards.
//
// The server hands back events with ever-increasing ids (server/feedback.js's
// getUpdatesFor); everything "seen" here is an event id, never a timestamp,
// so a wrong device clock can't hide or resurface anything. `baseline` is the
// newest event id that existed the first time this ran - history from before
// then is never shown as unread.
import { safeGet, safeSet } from './local-storage.js';
import { getFeedbackUpdates } from './feedback.js';

const STORAGE_KEY = 'halveren-feedback-seen';

let unreadByItem = new Map(); // itemId -> events[] (oldest first) not yet seen
const listeners = new Set();

function load() {
    try {
        const parsed = JSON.parse(safeGet(STORAGE_KEY));
        if (parsed && typeof parsed.baseline === 'number') {
            return { baseline: parsed.baseline, seen: parsed.seen || {}, toastedThrough: parsed.toastedThrough || 0 };
        }
    } catch (e) {}
    return null;
}

function save(stored) {
    safeSet(STORAGE_KEY, JSON.stringify(stored));
}

function notify() {
    listeners.forEach((cb) => cb(unreadItemCount()));
}

function unreadItemCount() {
    return unreadByItem.size;
}

export function onUnreadChange(cb) {
    listeners.add(cb);
    cb(unreadItemCount());
}

// Never throws - a failed refresh just leaves whatever was known before.
export async function refreshUnread() {
    try {
        let stored = load();
        if (!stored) {
            const { latestEventId } = await getFeedbackUpdates(0);
            stored = { baseline: latestEventId, seen: {}, toastedThrough: latestEventId };
            save(stored);
            unreadByItem = new Map();
        } else {
            const { events } = await getFeedbackUpdates(stored.baseline);
            const next = new Map();
            for (const event of events) {
                if (event.id <= (stored.seen[event.itemId] || 0)) continue;
                if (!next.has(event.itemId)) next.set(event.itemId, []);
                next.get(event.itemId).push(event);
            }
            unreadByItem = next;
        }
        notify();
    } catch (e) {}
}

export function getUnreadForItem(itemId) {
    return unreadByItem.get(Number(itemId)) || [];
}

export function markItemSeen(itemId) {
    const id = Number(itemId);
    const events = unreadByItem.get(id);
    if (!events) return;
    const stored = load();
    if (stored) {
        stored.seen[id] = events[events.length - 1].id;
        save(stored);
    }
    unreadByItem.delete(id);
    notify();
}

// The number of items to announce in a one-time toast, or 0 if everything
// unread has already been announced once (each event toasts at most once, so
// reloading the page doesn't repeat it).
export function takeToastCount() {
    const stored = load();
    if (!stored) return 0;
    let newest = stored.toastedThrough;
    const announced = new Set();
    for (const [itemId, events] of unreadByItem) {
        for (const event of events) {
            if (event.id > stored.toastedThrough) {
                announced.add(itemId);
                newest = Math.max(newest, event.id);
            }
        }
    }
    if (announced.size === 0) return 0;
    stored.toastedThrough = newest;
    save(stored);
    return announced.size;
}
